import {
  CountryEntity,
  FileTypeName,
  IdentityDocumentType,
} from 'api/types/entity';
import { GridFields } from 'components';
import { useTranslation } from 'libs/i18n';
import { Stack } from 'libs/ui';
import { Section } from 'modules/client/common/components';
import {
  CountryRiskBadges,
  CountryRiskField,
  DocumentsTableFeature,
} from 'modules/client/common/feature';
import {
  Field,
  useFormContext,
} from 'modules/client/common/forms/identityDocumentWithFilesForm';

interface Props {
  countriesLoading?: boolean;
  countries: CountryEntity[];
  enabledDocuments?: boolean;
  enabledDetails?: boolean;
  title?: string;
}

export const IdentityDocumentSection = ({
  countriesLoading,
  countries,
  enabledDocuments,
  enabledDetails = true,
  title,
}: Props) => {
  const { t } = useTranslation();
  const { values } = useFormContext();

  return (
    <Section
      datax="identityDocumentSection"
      title={title ?? t('client.identityDocument.title')}
    >
      <Stack spacing={24}>
        {enabledDetails && (
          <GridFields>
            <>
              <Field.Autocomplete
                fullWidth
                label={t('client.identityDocument.documenyType.label')}
                name="identityDocument.documentType"
                optionLabelKey="label"
                options={Object.values(IdentityDocumentType).map((v) => ({
                  label: t(
                    `client.identityDocument.documenyType.variants.${v}`
                  ),
                  value: v,
                }))}
                optionValueKey="value"
                placeholder={t(
                  'client.identityDocument.documenyType.placeholder'
                )}
                variant="select"
              />
              <Field.Text
                fullWidth
                label={t('client.identityDocument.number.label')}
                name="identityDocument.number"
                placeholder={t('client.identityDocument.number.placeholder')}
              />
              <Field.Autocomplete
                label={t('client.identityDocument.countryOfIssue.label')}
                loading={countriesLoading}
                name="identityDocument.countryOfIssue"
                optionLabelKey="name"
                options={countries}
                optionValueKey="id"
                placeholder={t(
                  'client.identityDocument.countryOfIssue.placeholder'
                )}
                renderOptionRightAdornment={(option) => <CountryRiskBadges
                  country={option as CountryEntity}
                  field={CountryRiskField.IdentityDocument}
                />}
                rightStartElement={
                  <CountryRiskBadges
                    country={values.identityDocument?.countryOfIssue}
                    field={CountryRiskField.IdentityDocument}
                  />
                }
              />
              <Field.Text
                fullWidth
                label={t('client.identityDocument.authority.label')}
                name="identityDocument.authority"
                placeholder={t('client.identityDocument.authority.placeholder')}
              />

              <Field.DatePicker
                fullWidth
                disableFuture={false}
                label={t('client.identityDocument.dateOfIssue.label')}
                name="identityDocument.dateOfIssue"
                placeholder={t(
                  'client.identityDocument.dateOfIssue.placeholder'
                )}
              />

              <Field.DatePicker
                fullWidth
                disableFuture={false}
                label={t('client.identityDocument.expirationDate.label')}
                name="identityDocument.expirationDate"
                placeholder={t(
                  'client.identityDocument.expirationDate.placeholder'
                )}
              />
            </>
          </GridFields>
        )}
        {enabledDocuments && (
          <DocumentsTableFeature
            fileType={FileTypeName.IdentityDocument}
            name="identityDocument.files"
          />
        )}
      </Stack>
    </Section>
  );
};
